import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';

import type { OidcCredentialInput, OidcCredentialViewModel } from '../api';
import type { AuthServerProfileDto, CredentialTypeDto } from '../endpoints';

/** The login type a credential serves (PTV2-1383); providers bound to it inherit this. */
const CREDENTIAL_TYPES: readonly CredentialTypeDto[] = ['CLIENT', 'TENANT_OPERATOR', 'CLUSTER_OPERATOR'];

/** (PTV2-1565) Authorization-server profile: plain OIDC, or a SMART-on-FHIR server that needs `aud`. */
const AUTH_SERVER_PROFILES: readonly AuthServerProfileDto[] = ['STANDARD_OIDC', 'SMART_ON_FHIR'];

interface Draft {
  label: string;
  /** '' is the non-selectable "pick a type" default on create (PTV2-1520): submit is blocked until set. */
  type: CredentialTypeDto | '';
  isDefault: boolean;
  issuerUri: string;
  wellKnownUrl: string;
  authServerProfile: AuthServerProfileDto;
  fhirAud: string;
  scopes: string;
  clientId: string;
  clientSecret: string;
}

const EMPTY_DRAFT: Draft = {
  label: '',
  type: '',
  isDefault: false,
  issuerUri: '',
  wellKnownUrl: '',
  authServerProfile: 'STANDARD_OIDC',
  fhirAud: '',
  scopes: 'openid profile email',
  clientId: '',
  clientSecret: '',
};

function draftFrom(row: OidcCredentialViewModel): Draft {
  return {
    label: row.label,
    type: row.type,
    isDefault: row.isDefault,
    issuerUri: row.issuerUri,
    wellKnownUrl: row.wellKnownUrl,
    authServerProfile: row.authServerProfile,
    fhirAud: row.fhirAud,
    scopes: row.scopes,
    clientId: '',
    clientSecret: '',
  };
}

interface FieldErrors {
  label?: string;
  type?: string;
  issuerUri?: string;
  clientId?: string;
  clientSecret?: string;
}

export function OidcCredentialFormDialog({
  open,
  initial,
  local = false,
  onOpenChange,
  onSubmit,
  pending,
  submitError,
}: {
  readonly open: boolean;
  /** Non-null = edit; null = create. */
  readonly initial: OidcCredentialViewModel | null;
  /**
   * Local-application mode (create only): the issuer + client id/secret are filled by the backend, so
   * only label / type / scopes / default are shown and the credential fields are hidden.
   */
  readonly local?: boolean;
  readonly onOpenChange: (next: boolean) => void;
  readonly onSubmit: (input: OidcCredentialInput) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});
  const isEdit = initial !== null;
  const fp = 'routes.admin.oidcCredentials.form';

  useEffect(() => {
    if (open) {
      setDraft(initial !== null ? draftFrom(initial) : EMPTY_DRAFT);
      setErrors({});
    }
  }, [open, initial]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]): void {
    setDraft((p) => ({ ...p, [key]: value }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: FieldErrors = {};
    if (draft.label.trim().length === 0) next.label = t(`${fp}.errors.labelRequired`);
    // PTV2-1520: type is required and has an empty default on create — block submit until chosen. On
    // edit the type is read-only (immutable), so this cannot fail there.
    if (!isEdit && draft.type === '') next.type = t(`${fp}.errors.typeRequired`);
    // Local mode: the backend supplies issuer + client id + secret, so those fields are neither shown
    // nor validated here.
    if (!local) {
      if (draft.issuerUri.trim().length === 0) next.issuerUri = t(`${fp}.errors.issuerRequired`);
      const clientIdRequired = !(initial?.hasClientId ?? false);
      if (clientIdRequired && draft.clientId.trim().length === 0)
        next.clientId = t(`${fp}.errors.clientIdRequired`);
      const secretRequired = !(initial?.hasSecret ?? false);
      if (secretRequired && draft.clientSecret.trim().length === 0)
        next.clientSecret = t(`${fp}.errors.secretRequired`);
    }
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit({
      label: draft.label,
      // Non-empty here: create is validated above, edit carries the immutable stored type.
      type: draft.type as CredentialTypeDto,
      isDefault: draft.isDefault,
      issuerUri: draft.issuerUri,
      wellKnownUrl: draft.wellKnownUrl,
      authServerProfile: draft.authServerProfile,
      fhirAud: draft.fhirAud,
      scopes: draft.scopes,
      clientId: draft.clientId,
      clientSecret: draft.clientSecret,
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t(`${fp}.editTitle`) : local ? t(`${fp}.createLocalTitle`) : t(`${fp}.createTitle`)}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} noValidate className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField id="oidc-label" label={t(`${fp}.fields.label`)} required error={errors.label}>
              {(f) => (
                <Input
                  {...f}
                  value={draft.label}
                  onChange={(e) => {
                    set('label', e.target.value);
                  }}
                  autoComplete="off"
                />
              )}
            </FormField>
            <FormField
              id="oidc-type"
              label={t(`${fp}.fields.type`)}
              hint={isEdit ? t(`${fp}.hints.typeImmutable`) : t(`${fp}.hints.type`)}
              required={!isEdit}
              error={errors.type}
            >
              {(f) =>
                isEdit ? (
                  // PTV2-1520: type is immutable after creation — show it read-only.
                  <Input
                    {...f}
                    value={draft.type === '' ? '' : t(`routes.admin.oidcCredentials.typeLabel.${draft.type}`)}
                    readOnly
                    disabled
                    title={t(`${fp}.hints.typeImmutable`)}
                    className="text-sm"
                  />
                ) : (
                  <Select
                    {...f}
                    value={draft.type}
                    onChange={(e) => {
                      set('type', e.target.value as CredentialTypeDto | '');
                    }}
                  >
                    <option value="" disabled>
                      {t(`${fp}.fields.typePlaceholder`)}
                    </option>
                    {CREDENTIAL_TYPES.map((ct) => (
                      <option key={ct} value={ct}>
                        {t(`routes.admin.oidcCredentials.typeLabel.${ct}`)}
                      </option>
                    ))}
                  </Select>
                )
              }
            </FormField>
            {local && (
              <p className="text-sm text-muted-foreground sm:col-span-2">
                {t(`${fp}.localHint`)}
              </p>
            )}
            {!local && (
              <>
            <FormField
              id="oidc-issuer"
              label={t(`${fp}.fields.issuerUri`)}
              hint={t(`${fp}.hints.issuerUri`)}
              required
              error={errors.issuerUri}
              className="sm:col-span-2"
            >
              {(f) => (
                <Input
                  {...f}
                  value={draft.issuerUri}
                  onChange={(e) => {
                    set('issuerUri', e.target.value);
                  }}
                  placeholder="https://issuer.example.com/"
                  autoComplete="off"
                  className="font-mono text-sm"
                />
              )}
            </FormField>
            <FormField
              id="oidc-well-known"
              label={t(`${fp}.fields.wellKnownUrl`)}
              hint={t(`${fp}.hints.wellKnownUrl`)}
              className="sm:col-span-2"
            >
              {(f) => (
                <Input
                  {...f}
                  value={draft.wellKnownUrl}
                  onChange={(e) => {
                    set('wellKnownUrl', e.target.value);
                  }}
                  placeholder="https://issuer.example.com/.well-known/openid-configuration"
                  autoComplete="off"
                  className="font-mono text-xs"
                />
              )}
            </FormField>
            <FormField
              id="oidc-auth-server-profile"
              label={t(`${fp}.fields.authServerProfile`)}
              hint={t(`${fp}.hints.authServerProfile`)}
              className="sm:col-span-2"
            >
              {(f) => (
                <Select
                  {...f}
                  value={draft.authServerProfile}
                  onChange={(e) => {
                    set('authServerProfile', e.target.value as AuthServerProfileDto);
                  }}
                >
                  {AUTH_SERVER_PROFILES.map((p) => (
                    <option key={p} value={p}>
                      {t(`routes.admin.oidcCredentials.authServerProfileLabel.${p}`)}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            {draft.authServerProfile === 'SMART_ON_FHIR' && (
              <FormField
                id="oidc-fhir-aud"
                label={t(`${fp}.fields.fhirAud`)}
                hint={t(`${fp}.hints.fhirAud`)}
                className="sm:col-span-2"
              >
                {(f) => (
                  <Input
                    {...f}
                    value={draft.fhirAud}
                    onChange={(e) => {
                      set('fhirAud', e.target.value);
                    }}
                    placeholder="https://fhir-ehr.example.com/r4/<tenant>"
                    autoComplete="off"
                    className="font-mono text-xs"
                  />
                )}
              </FormField>
            )}
            <FormField
              id="oidc-scopes"
              label={t(`${fp}.fields.scopes`)}
              hint={t(`${fp}.hints.scopes`)}
              className="sm:col-span-2"
            >
              {(f) => (
                <Input
                  {...f}
                  value={draft.scopes}
                  onChange={(e) => {
                    set('scopes', e.target.value);
                  }}
                  placeholder="openid profile email"
                  autoComplete="off"
                  className="font-mono text-sm"
                />
              )}
            </FormField>
            <FormField
              id="oidc-client-id"
              label={t(`${fp}.fields.clientId`)}
              hint={
                (initial?.hasClientId ?? false) ? t(`${fp}.hints.clientIdStored`) : t(`${fp}.hints.clientId`)
              }
              required={!(initial?.hasClientId ?? false)}
              error={errors.clientId}
            >
              {(f) => (
                <Input
                  {...f}
                  value={draft.clientId}
                  onChange={(e) => {
                    set('clientId', e.target.value);
                  }}
                  autoComplete="off"
                  className="font-mono text-sm"
                  placeholder={
                    (initial?.hasClientId ?? false) ? t(`${fp}.keepStoredPlaceholder`) : undefined
                  }
                />
              )}
            </FormField>
            <FormField
              id="oidc-client-secret"
              label={t(`${fp}.fields.clientSecret`)}
              hint={
                (initial?.hasSecret ?? false) ? t(`${fp}.hints.secretStored`) : t(`${fp}.hints.clientSecret`)
              }
              required={!(initial?.hasSecret ?? false)}
              error={errors.clientSecret}
            >
              {(f) => (
                <Input
                  {...f}
                  type="password"
                  value={draft.clientSecret}
                  onChange={(e) => {
                    set('clientSecret', e.target.value);
                  }}
                  autoComplete="off"
                  placeholder={(initial?.hasSecret ?? false) ? t(`${fp}.keepStoredPlaceholder`) : undefined}
                />
              )}
            </FormField>
              </>
            )}
          </div>

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.isDefault}
              onChange={(e) => {
                set('isDefault', e.target.checked);
              }}
              className="h-3.5 w-3.5"
            />
            {t(`${fp}.fields.isDefault`)}
          </label>

          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t(`${fp}.errors.submit`, { code: getErrorCodeForUi(submitError) })}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
              }}
              disabled={pending}
            >
              {t(`${fp}.cancel`)}
            </Button>
            <Button type="submit" loading={pending}>
              {isEdit ? t(`${fp}.save`) : local ? t(`${fp}.createLocal`) : t(`${fp}.create`)}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
